import { ActionIcon, Alert, Box, Button, Card, Group, Modal, Select, Stack, Text, Tooltip } from '@mantine/core'
import { IconCopy, IconSend } from '@/components/icons'
import {
  hasSdAdminValue,
  sdAdminFinalText,
} from '@/components/tickets/ticket-detail-page-helpers'
import type { TicketSdAdminApiAction, TicketSdAdminApiActionResponse } from '@/types/api'

type SdAdminActionItem = {
  key: TicketSdAdminApiAction
  label: string
}

type SdAdminEmailChoice = {
  action: TicketSdAdminApiAction
  emails: string[]
  sendToCustomer?: boolean
}

type TicketSdAdminCardProps = {
  actions: SdAdminActionItem[]
  busyAction: TicketSdAdminApiAction | null
  result: TicketSdAdminApiActionResponse | null
  emailChoice: SdAdminEmailChoice | null
  selectedEmail: string | null
  onSelectedEmailChange: (email: string | null) => void
  onCloseEmailChoice: () => void
  onRunAction: (
    action: TicketSdAdminApiAction,
    email?: string,
    sendToCustomer?: boolean,
  ) => void
  onCopy: (value: string) => void
  onUseText: (text: string) => void
}

export function TicketSdAdminCard({
  actions,
  busyAction,
  result,
  emailChoice,
  selectedEmail,
  onSelectedEmailChange,
  onCloseEmailChoice,
  onRunAction,
  onCopy,
  onUseText,
}: TicketSdAdminCardProps) {
  return (
    <>
      <Modal
        opened={emailChoice !== null}
        onClose={onCloseEmailChoice}
        title="Email для admin API"
        centered
      >
        <Stack gap="sm">
          <Select
            label="Email"
            data={emailChoice?.emails ?? []}
            value={selectedEmail}
            onChange={(value) => onSelectedEmailChange(value)}
            searchable
            nothingFoundMessage="Email не найден"
          />
          <Group justify="flex-end" gap="xs">
            <Button variant="default" onClick={onCloseEmailChoice}>
              Отмена
            </Button>
            <Button
              loading={busyAction === emailChoice?.action}
              disabled={!emailChoice || !selectedEmail}
              onClick={() => {
                if (!emailChoice || !selectedEmail) return
                onRunAction(
                  emailChoice.action,
                  selectedEmail,
                  emailChoice.sendToCustomer === true,
                )
              }}
            >
              Продолжить
            </Button>
          </Group>
        </Stack>
      </Modal>

      {actions.length > 0 ? (
        <Card withBorder padding="sm" radius="md">
          <Text fw={600} size="xs" mb={8}>
            Админ API
          </Text>
          <Stack gap="xs">
            {actions.map((item) => (
              <Button
                key={item.key}
                size="compact-xs"
                variant="light"
                fullWidth
                leftSection={<IconSend size={14} stroke={1.5} />}
                loading={busyAction === item.key}
                disabled={busyAction !== null && busyAction !== item.key}
                onClick={() => onRunAction(item.key)}
              >
                {item.label}
              </Button>
            ))}
            {result ? (
              <SdAdminResult
                result={result}
                busyAction={busyAction}
                onCopy={onCopy}
                onUseText={onUseText}
                onRunAction={onRunAction}
              />
            ) : null}
          </Stack>
        </Card>
      ) : null}
    </>
  )
}

function SdAdminResult({
  result,
  busyAction,
  onCopy,
  onUseText,
  onRunAction,
}: {
  result: TicketSdAdminApiActionResponse
  busyAction: TicketSdAdminApiAction | null
  onCopy: (value: string) => void
  onUseText: (text: string) => void
  onRunAction: (
    action: TicketSdAdminApiAction,
    email?: string,
    sendToCustomer?: boolean,
  ) => void
}) {
  const finalText = sdAdminFinalText(result)
  const canUseText = finalText.length > 0 && Boolean(result.message_text)

  return (
    <Alert color={hasSdAdminValue(result) ? 'green' : 'yellow'} variant="light" p="xs">
      <Stack gap={8}>
        <Group gap={6} justify="space-between" align="center" wrap="nowrap">
          <Text size="xs" fw={600}>
            {result.sent_message_id ? 'Отправлено клиенту' : 'Текст для клиента'}
          </Text>
          {finalText ? (
            <Tooltip label="Копировать текст" withArrow>
              <ActionIcon
                size="xs"
                variant="subtle"
                aria-label="Копировать текст admin API"
                onClick={() => onCopy(finalText)}
              >
                <IconCopy size={12} stroke={1.5} />
              </ActionIcon>
            </Tooltip>
          ) : null}
        </Group>
        <Box
          style={{
            maxHeight: 180,
            overflowY: 'auto',
            overflowX: 'hidden',
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
            borderRadius: 6,
            padding: '8px 9px',
            background: 'var(--mantine-color-default)',
            border: '1px solid var(--mantine-color-default-border)',
          }}
        >
          <Text
            size="xs"
            style={{
              whiteSpace: 'inherit',
              overflowWrap: 'inherit',
              wordBreak: 'inherit',
            }}
          >
            {finalText || 'Данные не найдены в ответе admin API.'}
          </Text>
        </Box>
        {canUseText ? (
          <Group gap={6} wrap="wrap">
            <Button size="compact-xs" variant="default" onClick={() => onUseText(finalText)}>
              Вставить в ответ
            </Button>
            <Button
              size="compact-xs"
              variant="light"
              loading={busyAction === result.action}
              disabled={result.sent_message_id != null}
              onClick={() => onRunAction(result.action, undefined, true)}
            >
              {result.sent_message_id ? 'Отправлено' : 'Отправить клиенту'}
            </Button>
          </Group>
        ) : null}
      </Stack>
    </Alert>
  )
}
